import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useCameraStream } from '@/hooks/useCameraStream';
import { CameraScanner } from '@/components/prescriptions/CameraScanner';
import { ImagePreview } from '@/components/prescriptions/ImagePreview';
import { OcrResultConfirmation } from '@/components/prescriptions/OcrResultConfirmation';
import { PrescriptionMedicineSection } from '@/components/prescriptions/PrescriptionMedicineSection';

export const Step = {
  CAMERA: 'CAMERA',
  PREVIEW: 'PREVIEW',
  OCR_PROCESSING: 'OCR_PROCESSING',
  OCR_CONFIRM: 'OCR_CONFIRM',
  MEDICINES: 'MEDICINES',
};

export const PrescriptionIntakeFlow = ({
  role = 'ADMIN',
  onComplete,
  onManualEntry,
  onCancel,
}) => {
  const navigate = useNavigate();
  const camera = useCameraStream();

  const [currentStep, setCurrentStep] = useState(Step.CAMERA);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sourceType, setSourceType] = useState('camera'); // 'camera' | 'device'
  const [fileName, setFileName] = useState('prescription_capture.jpg');

  // OCR results and confirmed patient
  const [ocrResult, setOcrResult] = useState(null);
  const [confirmedPatient, setConfirmedPatient] = useState({ id: null, name: '', age: null, gender: '', phone: '' });
  const [medicines, setMedicines] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const defaultOnCancel = () => {
    camera.stopStream();
    if (onCancel) {
      onCancel();
    } else if (role === 'ADMIN') {
      navigate('/admin/prescriptions');
    } else {
      navigate('/user/dashboard');
    }
  };

  const defaultOnManualEntry = () => {
    camera.stopStream();
    if (onManualEntry) {
      onManualEntry();
    } else if (role === 'ADMIN') {
      navigate('/admin/prescriptions/new');
    } else {
      navigate('/user/dashboard');
    }
  };

  // Handle frame captured from camera
  const handleCameraCapture = (frameData) => {
    camera.stopStream();
    setCapturedBlob(frameData.blob);
    setPreviewUrl(frameData.dataUrl);
    setSourceType('camera');
    setFileName(`prescription_camera_${Date.now()}.jpg`);
    setCurrentStep(Step.PREVIEW);
  };

  // Handle device file upload
  const handleDeviceUpload = (file) => {
    camera.stopStream();
    const objectUrl = URL.createObjectURL(file);
    setCapturedBlob(file);
    setPreviewUrl(objectUrl);
    setSourceType('device');
    setFileName(file.name);
    setCurrentStep(Step.PREVIEW);
  };

  // Retake photo / re-open camera
  const handleRetake = () => {
    if (previewUrl && sourceType === 'device') {
      URL.revokeObjectURL(previewUrl);
    }
    setCapturedBlob(null);
    setPreviewUrl(null);
    setOcrResult(null);
    setCurrentStep(Step.CAMERA);
  };

  // Send image to backend OCR endpoint
  const handleProceedWithOCR = async () => {
    if (!capturedBlob) {
      toast.error('No image selected for OCR processing.');
      return;
    }

    setCurrentStep(Step.OCR_PROCESSING);

    try {
      const formData = new FormData();
      formData.append('file', capturedBlob, fileName);

      const response = await api.post('/api/prescriptions/ocr-extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      setOcrResult(data);

      if (data.success && data.patient_name) {
        toast.success(`Patient name detected: ${data.patient_name}`);
      } else {
        toast.warning(data.message || "Couldn't identify patient name with high confidence.");
      }

      setCurrentStep(Step.OCR_CONFIRM);
    } catch (err) {
      console.error('OCR Extraction error:', err);
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to process prescription image.';
      toast.error(errMsg);
      setOcrResult({
        success: false,
        message: errMsg,
      });
      setCurrentStep(Step.OCR_CONFIRM);
    }
  };

  // Once user/doctor confirms or edits patient details -> resolve patient and transition to medicines step
  const handleNameConfirmed = async (patientDetails) => {
    const rawName = typeof patientDetails === 'string' ? patientDetails : patientDetails?.name;
    if (!rawName || !rawName.trim()) {
      toast.error('Please enter or confirm a valid patient name');
      return;
    }

    const cleanName = rawName.trim();
    const cleanAge = patientDetails?.age ? Number(patientDetails.age) : null;
    const cleanGender = patientDetails?.gender ? String(patientDetails.gender).trim() : '';
    const cleanPhone = patientDetails?.phone ? String(patientDetails.phone).trim() : '';

    setIsUploading(true);

    try {
      // Look up if patient exists in records (with masked phones)
      let patientId = null;
      try {
        const matchRes = await api.post('/api/patients/match', {
          name: cleanName,
        });
        const matchData = matchRes.data;
        if (matchData.exact_matches && matchData.exact_matches.length > 0) {
          patientId = matchData.exact_matches[0].id;
        } else if (matchData.fuzzy_matches && matchData.fuzzy_matches.length > 0 && matchData.fuzzy_matches[0].similarity >= 80) {
          patientId = matchData.fuzzy_matches[0].id;
        }
      } catch (err) {
        console.warn('Patient match query failed:', err);
      }

      // If no existing patient found, patientId remains null.
      // The backend will create the PatientSQL record inline during prescription upload.
      setConfirmedPatient({
        id: patientId || null,
        name: cleanName,
        age: cleanAge,
        gender: cleanGender,
        phone: cleanPhone,
      });
      setCurrentStep(Step.MEDICINES);
    } catch (error) {
      console.error('Error confirming patient:', error);
      toast.error('Failed to confirm patient information');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePerformUpload = async (medicinesToUpload = []) => {
    if (!capturedBlob) {
      toast.error('No prescription document captured');
      return;
    }

    setIsUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', capturedBlob, fileName);
      if (confirmedPatient.id) {
        uploadData.append('patient_id', String(confirmedPatient.id));
      }
      uploadData.append('patient_name', confirmedPatient.name);
      uploadData.append('notes', `Uploaded via Camera/OCR on ${new Date().toLocaleDateString()}`);

      if (confirmedPatient.age) {
        uploadData.append('age', String(confirmedPatient.age));
      }
      if (confirmedPatient.gender) {
        uploadData.append('gender', confirmedPatient.gender);
      }
      if (confirmedPatient.phone) {
        uploadData.append('phone', confirmedPatient.phone);
      }

      const validMeds = (medicinesToUpload || [])
        .filter((m) => m && m.name && m.name.trim())
        .map((m) => {
          const item = {
            name: m.name.trim(),
            dosage: m.dosage || '',
            frequency: m.frequency || '',
            duration: m.duration || '',
            erp_product_id: m.erp_product_id ? Number(m.erp_product_id) : null,
            dispensed_quantity:
              m.dispensed_quantity !== undefined && m.dispensed_quantity !== ''
                ? Number(m.dispensed_quantity)
                : 1.0,
          };
          const rawUnit = (m.dispensed_unit || m.unit || '').trim();
          if (rawUnit && rawUnit.toUpperCase() !== 'UNIT') {
            item.dispensed_unit = rawUnit;
          }
          return item;
        });

      if (validMeds.length > 0) {
        uploadData.append('medicines', JSON.stringify(validMeds));
      }

      const res = await api.post('/api/prescriptions/upload', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(
        validMeds.length > 0
          ? `Prescription with ${validMeds.length} medicine(s) uploaded successfully!`
          : 'Prescription uploaded successfully!'
      );
      camera.stopStream();

      if (onComplete) {
        onComplete(res.data);
      } else if (role === 'ADMIN') {
        navigate('/admin/prescriptions');
      } else {
        const presId = res.data?.id;
        navigate(presId ? `/user/prescriptions/${presId}` : '/user/dashboard');
      }
    } catch (error) {
      console.error('Error uploading prescription:', error);
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to upload prescription';
      toast.error(errMsg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={defaultOnCancel}
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight">
              Prescription Intake
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {role === 'USER'
                ? 'Capture prescription to initiate counter fulfillment directly'
                : 'Capture or upload prescription to extract patient name automatically'}
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={defaultOnManualEntry}
          className="text-xs hidden sm:flex items-center gap-1.5 h-9"
        >
          <FileText className="h-3.5 w-3.5" />
          <span>Manual Entry</span>
        </Button>
      </div>

      {/* Workflow Step Indicator */}
      <div className="w-full flex items-center justify-between px-2 text-xs font-medium text-muted-foreground">
        <div className={`flex items-center gap-1.5 ${currentStep === Step.CAMERA ? 'text-primary font-bold' : ''}`}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center bg-muted border border-border text-[11px]">1</span>
          <span>Capture</span>
        </div>
        <div className="h-px flex-1 mx-2 bg-border" />
        <div className={`flex items-center gap-1.5 ${currentStep === Step.PREVIEW || currentStep === Step.OCR_PROCESSING ? 'text-primary font-bold' : ''}`}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center bg-muted border border-border text-[11px]">2</span>
          <span>Preview</span>
        </div>
        <div className="h-px flex-1 mx-2 bg-border" />
        <div className={`flex items-center gap-1.5 ${currentStep === Step.OCR_CONFIRM ? 'text-primary font-bold' : ''}`}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center bg-muted border border-border text-[11px]">3</span>
          <span>Patient</span>
        </div>
        <div className="h-px flex-1 mx-2 bg-border" />
        <div className={`flex items-center gap-1.5 ${currentStep === Step.MEDICINES ? 'text-primary font-bold' : ''}`}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center bg-muted border border-border text-[11px]">4</span>
          <span>Medicines (Optional)</span>
        </div>
      </div>

      {/* Step 1: Camera Viewfinder */}
      {currentStep === Step.CAMERA && (
        <CameraScanner
          camera={camera}
          onCapture={handleCameraCapture}
          onDeviceUpload={handleDeviceUpload}
          onManualEntry={defaultOnManualEntry}
        />
      )}

      {/* Step 2: Image Preview */}
      {currentStep === Step.PREVIEW && previewUrl && (
        <ImagePreview
          imageSrc={previewUrl}
          sourceType={sourceType}
          onRetake={handleRetake}
          onConfirmImage={handleProceedWithOCR}
          onManualEntry={defaultOnManualEntry}
        />
      )}

      {/* Step 3: OCR Processing Loading Overlay */}
      {currentStep === Step.OCR_PROCESSING && (
        <div className="w-full max-w-xl mx-auto rounded-2xl border border-border bg-card p-12 text-center shadow-xl flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
            <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-amber-500 animate-pulse" />
          </div>
          <div>
            <h3 className="font-serif text-xl font-bold mb-1">Scanning Prescription</h3>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
              Applying image contrast, deskewing, and extracting patient name using conventional OCR...
            </p>
          </div>
        </div>
      )}

      {/* Step 4: Confirm Extracted Name */}
      {currentStep === Step.OCR_CONFIRM && (
        <OcrResultConfirmation
          result={ocrResult}
          isSubmitting={isUploading}
          onConfirm={handleNameConfirmed}
          onRetake={handleRetake}
          onManualEntry={defaultOnManualEntry}
        />
      )}

      {/* Step 5: Optional Medicines Section */}
      {currentStep === Step.MEDICINES && (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
          {/* Patient summary badge */}
          <div className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                Patient Assigned
              </p>
              <h3 className="font-serif text-lg font-bold text-foreground">
                {confirmedPatient.name}
              </h3>
              {(confirmedPatient.age || confirmedPatient.gender || confirmedPatient.phone) && (
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  {confirmedPatient.age && <span>{confirmedPatient.age} Yrs</span>}
                  {confirmedPatient.age && confirmedPatient.gender && <span>•</span>}
                  {confirmedPatient.gender && <span>{confirmedPatient.gender}</span>}
                  {(confirmedPatient.age || confirmedPatient.gender) && confirmedPatient.phone && <span>•</span>}
                  {confirmedPatient.phone && <span>Ph: {confirmedPatient.phone}</span>}
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isUploading}
              onClick={() => setCurrentStep(Step.OCR_CONFIRM)}
              className="text-xs h-8"
            >
              Back to Patient Info
            </Button>
          </div>

          {/* Reusable Medicine Section */}
          <PrescriptionMedicineSection
            medicines={medicines}
            onChange={setMedicines}
            optional={true}
          />

          {/* Action buttons: Skip vs Confirm & Upload */}
          <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isUploading}
              onClick={() => handlePerformUpload([])}
              className="w-full sm:w-auto h-11 px-5 text-sm"
            >
              Skip Medicines & Upload
            </Button>

            <Button
              type="button"
              disabled={isUploading}
              onClick={() => handlePerformUpload(medicines)}
              className="w-full sm:w-auto h-11 px-6 text-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-md flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <span>Confirm & Upload Prescription</span>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionIntakeFlow;

